import { z } from 'zod'

/**
 * Attaching an article by hand (CLI-625), under the queries every reader matches on.
 *
 * Design B (Kevin, 2026-10-08): the attach is written straight into the warehouse — the hit and
 * one `hit_queries` row per query id, by the data API's `POST /api/news/manual` — so every reader
 * that resolves a topic to its query ids (the report pull, the RTA runner, the analytics cache
 * job) serves it with no change of its own. `public.hit_additions` keeps the provenance: who,
 * why, under which topic and query.
 *
 * Two independent lists say WHERE it lands (Kevin, same day: "they can add a query id and attach
 * it to multiple topics even if that query is not in that topic"):
 * - `topicIds` — each named topic of this team contributes its live news queries, so the article
 *   shows wherever that topic is read;
 * - `queryIds` and `queries` — one query each, by id (one of the team's live queries) or by text
 *   (any term; the id is derived from it). A query row reaches every topic holding that query, in
 *   any client, and nothing else — so a term none of this team's topics hold is visible to other
 *   clients sharing it and to nobody here. The response names the queries it wrote under.
 */

/** Which product the attach was made from. */
export const ADDITION_SOURCES = ['headline', 'rta', 'api'] as const
export const additionSourceSchema = z.enum(ADDITION_SOURCES)
export type AdditionSource = z.infer<typeof additionSourceSchema>

/** Why. Optional: a reason nobody chose is better recorded as absent than guessed at. */
export const ADDITION_REASONS = [
  'client_request',
  'missed_by_matcher',
  'competitor',
  'context',
  'other',
] as const
export const additionReasonSchema = z.enum(ADDITION_REASONS)
export type AdditionReason = z.infer<typeof additionReasonSchema>

/**
 * Which family writes it and which pulls serve it: news, X or podcast. Read off the URL's host
 * when the person does not say (x.com and twitter.com are X; the podcast hosts are podcast;
 * everything else is news). Broadcast and newsletter are not attachable by URL.
 */
/**
 * Every source the data API has a writer for (its `POST /api/manual/items`): the six families.
 * LinkedIn is written like news; a broadcast clip lands under the saved search its topic's
 * query text resolves to; a newsletter under its sender.
 */
export const ATTACH_SOURCES = [
  'news',
  'x',
  'linkedin',
  'broadcast',
  'podcast',
  'newsletter',
] as const
export const attachSourceSchema = z.enum(ATTACH_SOURCES)
export type AttachSource = z.infer<typeof attachSourceSchema>

/** The data API's own caps per call and per article. */
export const ATTACH_ARTICLES_MAX = 100
export const ATTACH_QUERIES_MAX = 50

const count = z.number().int().nonnegative().max(1_000_000_000_000)

/**
 * Everything a person may know about an article besides its link and title. Which source asks
 * for which is `MANUAL_SHAPES` (attach-fields.ts); each field reaches the warehouse — the hit
 * row, its full text, its snippets, or the family's own metadata — at parity with what the
 * legacy console and manual alert took. Empty means absent: a screen omits what was not typed.
 */
export const attachArticleExtrasSchema = z.object({
  /** Outlet, account name, station, podcast or newsletter — whatever the source calls it. */
  outlet: z.string().trim().min(1).max(255).optional(),
  /** Byline, host or program. */
  author: z.string().trim().min(1).max(255).optional(),
  /** A social handle, with or without the @. */
  handle: z.string().trim().min(1).max(80).optional(),
  publishDate: z.iso.datetime().optional(),
  language: z.string().trim().min(2).max(16).optional(),
  country: z.string().trim().length(2).optional(),
  /** The line under the headline: the hit's description. */
  description: z.string().trim().min(1).max(4000).optional(),
  /** The lines a report quotes, in order: one `snippets` row per query link. */
  snippets: z.array(z.string().trim().min(1).max(4000)).max(10).optional(),
  /** Full text — an article's body, an episode's transcript: the hit's content. */
  content: z.string().trim().min(1).max(200_000).optional(),
  /** X: the counts the platform shows. */
  followers: count.optional(),
  views: count.optional(),
  likes: count.optional(),
  reposts: count.optional(),
  replies: count.optional(),
  /** Podcast: the show's audience estimate. */
  audience: count.optional(),
  /** X only: the post's text when it is not the title; the title stands in otherwise. */
  text: z.string().max(10_000).optional(),
  /** Podcast only: the show's name; `outlet` stands in otherwise. */
  podcastName: z.string().trim().min(1).max(255).optional(),
  /**
   * Newsletter only: the sender's address, which is what a newsletter topic matches on. A topic
   * named on the attach lends its first sender when this is blank.
   */
  sender: z.string().trim().toLowerCase().email().max(255).optional(),
})
export type AttachArticleExtras = z.infer<typeof attachArticleExtrasSchema>

/** The article, as the person has it. The warehouse finds or creates the hit by URL. */
export const attachArticleSchema = attachArticleExtrasSchema.extend({
  url: z.string().url().max(2000),
  /** Overrides what the URL's host says. */
  source: attachSourceSchema.optional(),
  title: z.string().trim().min(1).max(1000),
})
export type AttachArticle = z.infer<typeof attachArticleSchema>

export const attachArticlesSchema = z
  .object({
    articles: z.array(attachArticleSchema).min(1).max(ATTACH_ARTICLES_MAX),
    /** This team's topics; each contributes its live news queries. */
    topicIds: z.array(z.uuid()).max(ATTACH_QUERIES_MAX).default([]),
    /** Ids of this team's live queries, attached one by one. */
    queryIds: z.array(z.uuid()).max(ATTACH_QUERIES_MAX).default([]),
    /** Query terms as text; the id is derived, so any term is allowed. */
    queries: z.array(z.string().trim().min(1).max(500)).max(ATTACH_QUERIES_MAX).default([]),
    reason: additionReasonSchema.optional(),
    note: z.string().max(2000).optional(),
    source: additionSourceSchema.default('headline'),
  })
  .refine((body) => body.topicIds.length + body.queryIds.length + body.queries.length > 0, {
    message: 'Name at least one topic or query to attach under.',
  })
export type AttachArticles = z.infer<typeof attachArticlesSchema>

/** One query the attach was written under, and which of the named topics carried it. */
export const attachedQuerySchema = z.object({
  id: z.uuid(),
  source: attachSourceSchema,
  text: z.string(),
  topicIds: z.array(z.uuid()),
})
export type AttachedQuery = z.infer<typeof attachedQuerySchema>

export const attachArticleOutcomeSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('attached'),
    url: z.string(),
    source: attachSourceSchema,
    hitId: z.uuid(),
    hitCreatedAt: z.string(),
    /** The warehouse already held this URL; the queries were linked to that hit. */
    existed: z.boolean(),
    /** Query links this call wrote (0 when every one already stood). */
    linkedQueries: z.number().int(),
    /** Provenance rows written to `public.hit_additions`, one per query and topic. */
    additionIds: z.array(z.uuid()),
  }),
  z.object({
    status: z.literal('failed'),
    url: z.string(),
    source: attachSourceSchema,
    error: z.string(),
  }),
])
export type AttachArticleOutcome = z.infer<typeof attachArticleOutcomeSchema>

export const attachArticlesResponseSchema = z.object({
  queries: z.array(attachedQuerySchema),
  results: z.array(attachArticleOutcomeSchema),
  attached: z.number().int(),
  failed: z.number().int(),
})
export type AttachArticlesResponse = z.infer<typeof attachArticlesResponseSchema>

/** A provenance row, as the listing describes it. */
export const articleAdditionSchema = z.object({
  id: z.uuid(),
  hitId: z.uuid().nullable(),
  url: z.string().nullable(),
  title: z.string().nullable(),
  outlet: z.string().nullable(),
  publishDate: z.string().nullable(),
  author: z.string().nullable(),
  scope: z.string(),
  scopeId: z.string().nullable(),
  topicId: z.uuid().nullable(),
  queryId: z.uuid().nullable(),
  reason: z.string().nullable(),
  note: z.string().nullable(),
  source: z.string(),
  actor: z.string(),
  createdAt: z.string(),
})
export type ArticleAddition = z.infer<typeof articleAdditionSchema>

export const articleAdditionsResponseSchema = z.object({
  additions: z.array(articleAdditionSchema),
})
export type ArticleAdditionsResponse = z.infer<typeof articleAdditionsResponseSchema>
